/**
 * src/api/config-pin/ConfigPinApi.ts — Code d'accès à la section Configuration.
 *
 * Seul point de contact Supabase du module (invariant 4.B.2). Le tenant est résolu
 * côté base (get_my_tenant_id) ; aucune donnée tenant envoyée depuis le client.
 * Mode strict (flag `securite_pin_config`) : pas de code par défaut, essais limités.
 */
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { ok, err, type Result } from '@/api/result';
import { type AppError, appErrorFromSupabase, unknownError } from '@/api/errors';

const StatusSchema = z.object({
  strict: z.boolean(),
  has_pin: z.boolean(),
  can_manage: z.boolean(),
  locked_until: z.string().nullable(),
});

export type ConfigPinStatus = {
  strict: boolean;
  hasPin: boolean;
  canManage: boolean;
  lockedUntil: string | null;
};

export const ConfigPinApi = {
  async status(): Promise<Result<ConfigPinStatus, AppError>> {
    const { data, error } = await supabase.rpc('config_pin_status');
    if (error) return err(appErrorFromSupabase(error, 'ConfigPinApi.status'));
    const p = StatusSchema.safeParse(data);
    if (!p.success) return err(unknownError('Réponse inattendue (statut du code).'));
    return ok({
      strict: p.data.strict,
      hasPin: p.data.has_pin,
      canManage: p.data.can_manage,
      lockedUntil: p.data.locked_until,
    });
  },

  async verify(pin: string): Promise<Result<boolean, AppError>> {
    const { data, error } = await supabase.rpc('verify_config_pin', { p_pin: pin });
    if (error) return err(appErrorFromSupabase(error, 'ConfigPinApi.verify'));
    return ok(data === true);
  },

  /** Définit ou change le code. `oldPin` vide pour le tout premier code en mode strict. */
  async set(oldPin: string, newPin: string): Promise<Result<true, AppError>> {
    const { data, error } = await supabase.rpc('set_config_pin', {
      p_old_pin: oldPin,
      p_new_pin: newPin,
    });
    if (error) return err(appErrorFromSupabase(error, 'ConfigPinApi.set'));
    const res = data as { success?: boolean; error?: string } | null;
    if (!res?.success) return err(unknownError(res?.error ?? 'Changement du code refusé.'));
    return ok(true);
  },
} as const;
